import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer, Truck, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

/**
 * The morning screen: load out the vans, then print each driver's sheet.
 *
 * Assigning used to be one dropdown per parcel, which is 40-odd of them before
 * anything could be printed — so everything waiting is listed here with tick
 * boxes and assigned in one go.
 */
export default function Runsheet() {
  const qc = useQueryClient();
  const [picked, setPicked] = useState({});
  const [driverId, setDriverId] = useState('');
  const [busy, setBusy] = useState(false);
  const [printing, setPrinting] = useState(null);

  const { data: drivers = [] } = useQuery({
    queryKey: ['runsheet-drivers'],
    queryFn: () => api.get('/reports/runsheet/drivers').then((r) => r.data),
  });

  // Everything still to be delivered, whoever it belongs to
  const { data: parcels = [], isLoading } = useQuery({
    queryKey: ['runsheet-parcels'],
    queryFn: () => api.get('/parcels', { params: { limit: 500 } })
      .then((r) => (Array.isArray(r.data) ? r.data : r.data.parcels || [])
        .filter((p) => !['delivered', 'cancelled'].includes(p.status))
        // A parcel that has already failed a delivery is the one worth chasing
        // — usually a phone call before it goes out again.
        .sort((a, b) => (b.failed_attempts || 0) - (a.failed_attempts || 0))),
  });

  const chosen = Object.keys(picked).filter((k) => picked[k]);
  const toggle = (t) => setPicked((p) => ({ ...p, [t]: !p[t] }));
  const allShown = parcels.map((p) => p.tracking_number);
  const allTicked = allShown.length > 0 && allShown.every((t) => picked[t]);

  const assign = async (unassign = false) => {
    if (!chosen.length) return toast.error('Tick some parcels first');
    if (!unassign && !driverId) return toast.error('Choose a driver');
    setBusy(true);
    try {
      const { data } = await api.patch('/parcels/bulk-assign', {
        tracking_numbers: chosen,
        driver_id: unassign ? null : driverId,
        type: 'delivery',
      });
      toast.success(unassign
        ? `${data.count} taken off the van`
        : `${data.count} assigned to ${drivers.find((d) => d.id === driverId)?.name}`);
      setPicked({});
      qc.invalidateQueries({ queryKey: ['runsheet-parcels'] });
      qc.invalidateQueries({ queryKey: ['runsheet-drivers'] });
      qc.invalidateQueries({ queryKey: ['parcels'] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not assign those');
    } finally { setBusy(false); }
  };

  // The endpoint is staff-only, so fetch it as a blob — a plain link carries
  // no auth header.
  const print = async (d) => {
    setPrinting(d.id);
    try {
      const res = await api.get('/reports/runsheet', {
        params: { driver_id: d.id }, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error(`Could not build ${d.name}'s runsheet`);
    } finally { setPrinting(null); }
  };

  const noAddress = parcels.filter((p) => !p.recipient_address).length;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-800">Runsheets</h1>
        <p className="text-xs text-gray-400">
          Load the vans, then print each driver a sheet to carry — it works with a flat battery or no signal.
        </p>
      </div>

      {noAddress > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">{noAddress} of {parcels.length} stops have no address saved.</p>
            <p className="text-xs mt-1 text-amber-800">
              Those print with the name and cell number only, which is not enough to find the place.
              Add the address on the client — use the address box with autocomplete and it saves the
              map pin too, which is also what puts the stop in the right place in the route order.
            </p>
          </div>
        </div>
      )}

      {/* Drivers + print */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {drivers.map((d) => {
          const total = Number(d.deliveries) + Number(d.pickups);
          return (
            <div key={d.id} className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Truck size={16} className="text-orange-500" />
                  <span className="font-semibold text-gray-800">{d.name.trim()}</span>
                </div>
                <span className="text-xs text-gray-400">{total} stop{total === 1 ? '' : 's'}</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                {d.deliveries} deliver{Number(d.deliveries) === 1 ? 'y' : 'ies'} · {d.pickups} pickup{Number(d.pickups) === 1 ? '' : 's'}
              </p>
              <button onClick={() => print(d)} disabled={printing === d.id || total === 0}
                className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-semibold disabled:opacity-40">
                <Printer size={15} />
                {printing === d.id ? 'Building…' : total === 0 ? 'Nothing to print' : 'Print runsheet'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Assign */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wide flex-1">
            Waiting to go out{chosen.length > 0 && <span className="text-orange-600 normal-case"> — {chosen.length} ticked</span>}
          </h2>
          <select value={driverId} onChange={(e) => setDriverId(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
            <option value="">— Choose driver —</option>
            {drivers.map((d) => <option key={d.id} value={d.id}>{d.name.trim()}</option>)}
          </select>
          <button onClick={() => assign(false)} disabled={busy || !chosen.length}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40">
            {busy ? 'Assigning…' : `Assign ${chosen.length || ''}`}
          </button>
          {chosen.length > 0 && (
            <button onClick={() => assign(true)} disabled={busy}
              className="border rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
              Take off van
            </button>
          )}
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
        ) : parcels.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">Nothing waiting — everything is delivered.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox" checked={allTicked} className="accent-orange-500"
                      onChange={(e) => setPicked(e.target.checked
                        ? Object.fromEntries(allShown.map((t) => [t, true])) : {})} />
                  </th>
                  <th className="px-3 py-2 text-left">Tracking</th>
                  <th className="px-3 py-2 text-left">Receiver</th>
                  <th className="px-3 py-2 text-left">Address</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Driver</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {parcels.map((p) => (
                  <tr key={p.id || p.tracking_number}
                    className={`hover:bg-orange-50 cursor-pointer ${picked[p.tracking_number] ? 'bg-orange-50' : ''}`}
                    onClick={() => toggle(p.tracking_number)}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={!!picked[p.tracking_number]}
                        onChange={() => toggle(p.tracking_number)} className="accent-orange-500" />
                    </td>
                    <td className="px-3 py-2 font-mono font-semibold text-gray-800">
                      {p.tracking_number}
                      {p.box_count > 1 && (
                        <span className="ml-2 text-[10px] font-sans bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5">
                          {p.box_count} boxes
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-800">{p.recipient_name}</td>
                    <td className="px-3 py-2 text-gray-500">
                      {p.recipient_address || <span className="text-amber-600">no address</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-500">
                      {String(p.status).replace(/_/g, ' ')}
                      {/* A parcel back from the cage looks identical to a fresh
                          one here, so say it has already been out and failed. */}
                      {p.failed_attempts > 0 && (
                        <span className="ml-2 text-[10px] bg-red-100 text-red-700 rounded-full px-1.5 py-0.5"
                          title={p.last_failed_at ? `Last tried ${new Date(p.last_failed_at).toLocaleString('en-ZA')}` : ''}>
                          {p.failed_attempts} failed {p.failed_attempts === 1 ? 'try' : 'tries'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{p.driver_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
